import { useCallback } from 'react'
import { useEnrollmentStore } from '@/stores'
import type { ApplicationFormState, FieldErrorKey, UpdateApplicationField } from '../types'
import { formatPhoneNumber } from '../utils'
import { useApplicationFormDraftActions } from './fieldActions/useApplicationFormDraftActions'
import { useApplicationFormSelectionActions } from './fieldActions/useApplicationFormSelectionActions'

type UseApplicationFormFieldActionsArgs = {
  state: ApplicationFormState
}

export function useApplicationFormFieldActions({
  state
}: UseApplicationFormFieldActionsArgs) {
  const updateEnrollmentDraft = useEnrollmentStore(store => store.updateFormData)

  const clearFieldError = useCallback(
    (field: FieldErrorKey) => {
      state.setFieldErrors(errors => ({ ...errors, [field]: undefined }))
    },
    [state]
  )

  const updateField = useCallback<UpdateApplicationField>(
    (field, value) => {
      state.setFormData(previous => ({ ...previous, [field]: value }))
    },
    [state]
  )

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      updateField('phoneNumber', formatPhoneNumber(value))
    },
    [updateField]
  )

  const handleBirthDatePickerChange = useCallback(
    (value: string) => {
      if (!value) {
        updateField('birthDate', '')
        return
      }

      const [year, month, day] = value.split('-')
      updateField('birthDate', `${month}/${day}/${year}`)
    },
    [updateField]
  )

  const draftActions = useApplicationFormDraftActions({
    clearFieldError,
    state,
    updateEnrollmentDraft,
    updateField
  })
  const selectionActions = useApplicationFormSelectionActions({
    clearFieldError,
    state,
    updateEnrollmentDraft,
    updateField
  })

  return {
    clearFieldError,
    handleBirthDatePickerChange,
    handlePhoneNumberChange,
    ...draftActions,
    ...selectionActions,
    updateField
  }
}
